'use client';

import { useDropdown } from '@/context/DropdownContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useOutsideClick from '@/hooks/useOnClickOutside';
import { Button } from '@ama-pt/agora-design-system';
import { useTranslations } from 'next-intl';
import React, { useRef, useState, useEffect } from 'react';
import { InputSearchBar, InputSearchBarElement } from "@ama-pt/agora-design-system";
import { SearchTrendItem } from '@/services/search-trends/types';

const SearchPanel: React.FC<{ popularLinks: SearchTrendItem[] }> = ({ 
  popularLinks
}) => {
  const translate = useTranslations();
  const t = useTranslations('SearchPanel');
  const [searchValue, setSearchValue] = useState('');
  const [isListening, setIsListening] = useState(false);
  const searchRef = useRef<InputSearchBarElement>(null);

  const { isTablet, isMobile } = useMediaQuery();
  const panelRef = useRef<HTMLDivElement>(null);
  
  const { activeDropdown, setActiveDropdown } = useDropdown();
  const dropdownId = 'search-panel';
  const isOpen = activeDropdown === dropdownId;
  
  useOutsideClick(panelRef as any, () => {
    if (isOpen) {
      setActiveDropdown(null);
    }
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setActiveDropdown(null);
        const searchButton = panelRef.current?.querySelector('button') as HTMLElement;
        searchButton?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggleDropdown = () => {
    setActiveDropdown(isOpen ? null : dropdownId);
  };

  useEffect(() => {
    if (!isOpen) return;

    const focusSearchInput = () => {
      const searchInput = panelRef.current?.querySelector<HTMLInputElement>(
        '#search-bar-input input, input#search-bar-input, input'
      );

      searchInput?.focus();
    };

    window.requestAnimationFrame(() => {
      window.setTimeout(focusSearchInput, 0);
    });
  }, [isOpen]);

  const handlePanelBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null;

    if (isOpen && nextFocusedElement && !event.currentTarget.contains(nextFocusedElement)) {
      setActiveDropdown(null);
    }
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      setActiveDropdown(null);
      
      window.location.href = `/pesquisar?search=${encodeURIComponent(searchValue.trim())}&page=0`;
    }
  };

  const handleVoiceSearch = () => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      alert("Seu navegador não suporta reconhecimento de voz. Tente usar Chrome, Edge ou Safari.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "pt-PT";
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchValue(transcript);
      
      setTimeout(() => {
        setActiveDropdown(null);
        window.location.href = `/pesquisar?search=${encodeURIComponent(transcript.trim())}&page=0`;
      }, 100);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event: any) => {
      console.error("Erro no reconhecimento de voz:", event.error);
      setIsListening(false);

      switch (event.error) {
        case "network":
          alert("Erro de rede. Verifique sua conexão com a internet.");
          break;
        case "not-allowed":
          alert("Permissão de microfone negada. Ative o microfone nas configurações do navegador.");
          break;
        case "no-speech":
          alert("Nenhuma fala detectada. Tente novamente.");
          break;
        case "audio-capture":
          alert("Microfone não encontrado. Verifique se está conectado.");
          break;
        default:
          alert(`Erro no reconhecimento de voz: ${event.error}`);
      }
    };

    try {
      recognition.start();
    } catch (error) {
      console.error("Erro ao iniciar reconhecimento:", error);
      setIsListening(false);
      alert("Erro ao iniciar reconhecimento de voz. Tente novamente.");
    }
  };

  const handlePopularLinkClick = (searchTerm: string) => {
    setActiveDropdown(null);
    window.location.href = `/pesquisar?search=${encodeURIComponent(searchTerm.trim())}&page=0`;
  };



  const handlePopularLinkKeyDown = (event: React.KeyboardEvent, searchTerm: string) => {
    switch (event.key) {
      case 'Enter':
      case ' ': // Space
        event.preventDefault();
        handlePopularLinkClick(searchTerm);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextButton = (event.currentTarget as HTMLElement).closest('li')?.nextElementSibling?.querySelector('button') as HTMLElement;
        if (nextButton) {
          nextButton.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        const prevButton = (event.currentTarget as HTMLElement).closest('li')?.previousElementSibling?.querySelector('button') as HTMLElement;
        if (prevButton) {
          prevButton.focus();
        }
        break;
      case 'Escape':
        event.preventDefault();
        setActiveDropdown(null);
        break;
    }
  };
  
  return (
    <div ref={panelRef} className="panel-menu search-panel-menu" onBlur={handlePanelBlur}>
      <Button
        aria-haspopup="true"
        aria-label={t("searchPanel")}
        aria-expanded={isOpen}
        aria-controls="panel-content-Search"
        hasIcon
        leadingIcon={isOpen ? 'agora-line-x' : 'agora-line-search'}
        leadingIconHover={isOpen ? 'agora-line-x' : 'agora-solid-search'}
        className={`flex items-center justify-center agora-btn agora-btn-link-neutral ${isOpen ? 'panel-menu-button has-panel expanded' : ''}`}
        onClick={handleToggleDropdown}
      >
        <span className="children-wrapper hidden md:block">{t("searchPanel")}</span>
      </Button>
      
      <div 
        className={`panel-menu-content !bg-[#F9F9FA] ${isOpen ? '' : 'collapsed'}`}
        style={isTablet || isMobile ? {
          position: 'absolute',
          zIndex: 50,
          maxHeight: 'calc(100vh - 60px)'
        } : undefined}
      >
        <div 
          className="panel-menu-content-children" 
          id="panel-content-Search"
        >
          <div className="search-layout">
            <div className="search-input-bar-container custom-input">
              <p id="search-bar-header-title" className="mb-32 xs:text-l-bold md:text-xl-bold text-primary-900">
                {t("labelSearchPanel")}
              </p>
              
              <div className="relative label-search-bar-header">
                <div className="">
                  <InputSearchBar 
                    ref={searchRef}
                    id="search-bar-input"
                    className="border-0 text-primary-500 border-none text-m-light !border-white"
                    placeholder={t("placeholderSearch") || "Pesquisar"}
                    label={translate("SearchPanel.searchPanel")}
                    searchActionAltText={t("search") || "Pesquisar"}
                    
                    voiceActionAltText={
                      isListening 
                        ? "Escutando..." 
                        : (t("searchWithVoice") || "Pesquisar por voz")
                    }
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    onSearchActivate={handleSearch}
                    onVoiceActivate={handleVoiceSearch}
                  />

                  {isListening && (
                    <div className="mt-16 flex items-center justify-center text-blue-600">
                      <div className="animate-pulse flex items-center">
                        <svg className="w-20 h-20 mr-8" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M7 4a3 3 0 616 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 715 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm font-medium">
                          {t("labelVoice") || "Escutando..."}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="search-related-links-container">
              <div className="search-related-links-container-wrapper">
                <div className="search-list-title">
                  <p className="xs:text-m-bold md:text-l-bold text-primary-900">
                    {t("labelListService")}
                  </p>
                </div>
                
                <ul className="agora-list agora-list-unordered list-none search-list">
                  {popularLinks && popularLinks.map((link: SearchTrendItem, index) => (
                    <li key={index}>
                      <div className="list-items">
                        <div className="agora-link-wrapper agora-link-wrapper-link-neutral full-width inline-flex items-center justify-center min-h-[44px] min-w-[44px] py-8 custom-header-link-wrapper">
                          <button
                            type="button"
                            className="w-full text-left hover:text-primary-600 !outline-none transition-colors"
                            onClick={() => handlePopularLinkClick(link.title)}
                            onKeyDown={(e) => handlePopularLinkKeyDown(e, link.title)}
                          >
                            <span className="children-wrapper">{link.title}</span>
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          <div className="panel-menu-backdrop" onClick={() => setActiveDropdown(null)}></div>
        </div>
      </div>
    </div>
  );
};

export default SearchPanel;
